import { observable, action, runInAction, computed, IObservableArray, makeObservable } from "mobx";
import { projectsDataStore } from "Stores/ProjectsStore";
import { API } from "General/API";
import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { download } from "utils/download";
import { API_URL } from "General/APIEndpoints";
import { ArrayDataStore } from "Stores/RESTStoreInstances/ArrayDataStore";
import { emailUnreadStore } from "./EmailUnreadStore";

interface EmailTypeStoreConstructorOpts {
  type: API_MAILRESPONSE_TYPE;
  projectSpecific: boolean;
  isProjectInbox: boolean;
  emailInboxId: string | number;
}

export type MailSortBy = keyof Pick<
  API_MailResponse,
  "hasAttachments" | "timestamp" | "subject" | "importance" | "from"
>;

const sortByMap: { [key in MailSortBy]?: API_MAILPAGINATION_SORTBY } = {
  hasAttachments: "ATTACHMENT",
  timestamp: "DATE",
  subject: "SUBJECT",
  importance: "IMPORTANCE",
  from: "NAME",
};

export class EmailTypeStore extends ArrayDataStore<API_MailResponse> {
  total: number = null;
  sortOrder: API_MAILPAGINATION_SORTORDER = "DESC";
  sortBy: MailSortBy = "timestamp";
  actionState: StoreState = "RAW";
  defaultAmount = 30;
  emailType: API_MAILRESPONSE_TYPE = null;
  projectSpecific: boolean = false;
  /* Project inbox feature: list of created filters for project emails */
  isProjectInbox: boolean = false; // triggers feature
  emailInboxId: string | number = null; // required to request list of emails for particular inbox list
  projectInboxes: boolean = false; // check if switch is changed in SearchBarWithActions component

  constructor({ type, projectSpecific, isProjectInbox, emailInboxId }: EmailTypeStoreConstructorOpts) {
    super();

    makeObservable(this, {
      total: observable,
      sortOrder: observable,
      sortBy: observable,
      actionState: observable,
      projectInboxes: observable,
      // clear: action,
      fetchSortedBy: action.bound,
      loadMoreData: action.bound,
      refresh: action.bound,
      updateEmailData: action.bound,
      allowedToDelete: computed,
      allowedToArchive: computed,
      allowedToUnArchive: computed,
      allowedToMoveToProject: computed,
      asArray: computed,
      fetchList: action.bound,
      switchProjectInboxes: action.bound,
      deleteEmails: action.bound,
      downloadPdf: action.bound,
      moveToArchived: action.bound,
      restoreFromArchived: action.bound,
      moveMailToProject: action.bound,
    });

    this.emailType = type;
    this.projectSpecific = projectSpecific;
    this.isProjectInbox = isProjectInbox;
    this.emailInboxId = emailInboxId;
    this.data = [] as IObservableArray<API_MailResponse>;
    this.projectInboxes = projectsDataStore.currentProject.mailSettings?.projectInboxes ? false : null;
  }

  url() {
    if (this.isProjectInbox) {
      return API_URL.PROJECT_INBOXES.getProjectInboxContent(projectsDataStore.projectId, this.emailInboxId);
    } else {
      const url = API_URL.MAILS.getMails(projectsDataStore.projectId, this.emailType);
      return this.projectSpecific ? url + "?project=true" : url;
    }
  }

  clear() {
    super.clear();
    this.data = [] as IObservableArray<API_MailResponse>;
    this.projectInboxes = false;
    this.sortBy = "timestamp";
    this.sortOrder = "DESC";
    this.total = 0;
    this.emailInboxId = undefined;
  }

  // makes request without changing the store state.
  @handleStoreState()
  async fetchList(params: EmailFetchParams) {
    try {
      params.topicInbox = this.projectInboxes;
      params.sortBy = sortByMap[this.sortBy];
      params.sortOrder = this.sortOrder;
      params.limit = params.limit || this.defaultAmount;
      const response: API_MultiMailResponse = await API.getData(this.url(), params);
      return response;
    } catch (err) {
      throw err;
    }
  }

  async fetchSortedBy(params: {
    topicInbox?: boolean;
    sortOrder: API_MAILPAGINATION_SORTORDER;
    sortBy: MailSortBy;
    limit?: number;
    offset?: number;
  }) {
    try {
      this.projectInboxes = params.topicInbox;
      this.sortBy = params.sortBy;
      this.sortOrder = params.sortOrder;
      const response: API_MultiMailResponse = await this.fetchList({
        topicInbox: params.topicInbox,
        limit: params.limit,
        offset: params.offset,
      });
      runInAction(() => {
        this.total = response.total;
        this.data = new Array(response.total) as IObservableArray<API_MailResponse>;
        if (response.items?.length > 0) {
          this.data.splice(params.offset, response.items.length, ...response.items);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async loadMoreData(params: { offset: number; limit: number }) {
    try {
      const response = await this.fetchList({ offset: params.offset, limit: params.limit });
      runInAction(() => {
        this.total = response.total;
        if (response.items?.length > 0) {
          this.data.splice(params.offset, response.items.length, ...response.items);
        }
      });
    } catch (error) {
      throw error;
    }
  }

  async switchProjectInboxes(projectInboxes?: boolean) {
    this.projectInboxes = projectInboxes;
    return await this.refresh();
  }

  async refresh() {
    return await this.fetchSortedBy({
      topicInbox: this.projectInboxes,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      limit: this.defaultAmount < this.data.length ? this.data.length : this.defaultAmount,
    });
  }

  updateEmailData(newData: API_MailResponse) {
    const oldData = this.find(newData.id, "id") || {};
    if (oldData) {
      this.edit(oldData.id, { ...oldData, unread: false, body: newData.body });
      if (oldData.unread) {
        emailUnreadStore.markRead(newData.projectSpecific ? "project" : "private");
      }
    }
  }

  @handleStoreState<EmailTypeStore>("actionState")
  async deleteEmails(emailIds: string[], mailType: API_MAILRESPONSE_TYPE = "INBOX") {
    try {
      await API.deleteData(API_URL.MAILS.delete_1(projectsDataStore.projectId, mailType), {
        project: this.projectSpecific,
        id: emailIds.toString(),
      });
      await emailUnreadStore.fetch();
      runInAction(() => {
        for (const id of emailIds) {
          this.delete(id);
        }
        this.total = this.total - emailIds.length;
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState<EmailTypeStore>("actionState")
  async downloadPdf(emailIds: string[]) {
    try {
      const formBody: API_MailPdfRequest[] = emailIds.map((item) => ({
        projectSpecific: this.projectSpecific,
        id: item,
        mailType: this.emailType,
      }));
      const file: File = await API.getBlobByPost(API_URL.MAILS.getPdf(projectsDataStore.projectId), formBody);
      download(file, file.name, file.type);
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState<EmailTypeStore>("actionState")
  async moveToArchived(emailIds: string[]) {
    try {
      await API.editData(
        API_URL.MAILS.archive(projectsDataStore.projectId, this.emailType),
        {},
        { id: emailIds.toString() }
      );
      await emailUnreadStore.fetch();
      runInAction(() => {
        for (const id of emailIds) {
          this.delete(id);
        }
        this.total = this.total - emailIds.length;
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState<EmailTypeStore>("actionState")
  async restoreFromArchived(emailIds: string[]) {
    try {
      await API.editData(
        API_URL.MAILS.restore(projectsDataStore.projectId, "ARCHIVED"),
        {},
        { id: emailIds.toString() }
      );
      await emailUnreadStore.fetch();
      runInAction(() => {
        for (const id of emailIds) {
          this.delete(id);
        }
        this.total = this.total - emailIds.length;
      });
    } catch (err) {
      throw err;
    }
  }

  @handleStoreState<EmailTypeStore>("actionState")
  async moveMailToProject(emailData: { [id: string]: { name: string; projectSpecific: boolean } }, folderId: number) {
    try {
      const formBody: API_MailMoveRequest = {
        folderId: folderId,
        mailMoveInfo: Object.keys(emailData).map((mailId) => ({
          mailId: mailId,
          mailFileName: emailData[mailId].name,
          mailProjectSpecific: !!emailData[mailId].projectSpecific,
          mailType: this.emailType,
        })),
      };
      await API.editData(API_URL.MAILS.move(projectsDataStore.projectId), formBody);
      await emailUnreadStore.fetch();
      runInAction(() => {
        for (const id of Object.keys(emailData)) {
          this.delete(id);
        }
        this.total = this.total - 1;
      });
    } catch (err) {
      throw err;
    }
  }

  async selectAll() {
    try {
      const response = await API.getData<API_MinimumMailInfo[]>(
        API_URL.MAILS.getAllMailsByType(projectsDataStore.projectId, this.emailType)
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDefaultFilenames(mailIds: string[]) {
    try {
      const formData: API_MailsFilenameRequest = {
        mailIds,
      };
      return await API.newData<API_MailIdAndFilename[]>(
        API_URL.MAILS.getFilenamesForMails(projectsDataStore.projectId),
        formData
      );
    } catch (error) {
      throw error;
    }
  }

  async getEmailById(emailId: string) {
    const foundInList = this.data.find((item) => item.id === emailId);
    if (foundInList) {
      return foundInList;
    } else {
      const response = await API.getData<API_MailResponse>(
        API_URL.MAILS.getMail(projectsDataStore.projectId, this.emailType, emailId)
      );
      return response;
    }
  }

  get allowedToDelete() {
    if (this.emailType === "INBOX") {
      // Only for inbox
      if (this.projectSpecific) {
        // If project inbox
        if (
          // If user has WRITE access to project inbox
          projectsDataStore.currentProject.mailSettings &&
          projectsDataStore.currentProject.mailSettings.projectInbox === "WRITE"
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        // allowed for private inbox
        return true;
      }
    } else if (this.emailType === "DRAFT") {
      return true;
    } else {
      return false;
    }
  }

  get allowedToArchive() {
    if (this.emailType === "INBOX" || this.emailType === "SENT") {
      // Only for inbox
      if (this.projectSpecific) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  get allowedToUnArchive() {
    return this.emailType === "ARCHIVED";
  }

  /**
   * Moving emails to project is allowed ony type INBOX or private SENT:
   * private inbox - all users
   * private sent - all users
   * project inbox - if mailSettings.projectInbox === 'WRITE'
   */
  get allowedToMoveToProject() {
    return (
      // if emails are of type INBOX or SENT
      (this.emailType === "INBOX" || this.emailType === "SENT") &&
      // and private (do not belong to project) - it's allowed
      !this.projectSpecific
      //  || ( Removed this part according to https://pmgredesign.atlassian.net/browse/DEV-5119?focusedCommentId=34511
      // // or user has required access level - it's allowed
      // projectsDataStore.currentProject.mailSettings &&
      // projectsDataStore.currentProject.mailSettings.projectInbox === 'WRITE'
      // )
    );
  }

  get asArray() {
    return (this.data as IObservableArray).slice();
  }
}

interface EmailFetchParams {
  topicInbox?: boolean;
  limit: number;
  offset?: number;
  after?: number;
  before?: number;
  sortBy?: API_MAILPAGINATION_SORTBY;
  sortOrder?: API_MAILPAGINATION_SORTORDER;
}
